import { useReactiveVar } from '@apollo/client';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { isMobile } from 'react-device-detect';
import { type Location } from 'react-router';
import { authVar } from 'state/vars';
import { type Icons } from 'ui/atoms/Icon/Icon';

export interface MenuItem {
  location: string;
  text: string;
  icon: Icons;
  isSelected: boolean;
}

interface Props {
  translation: Translation;
  location: Location;
}

const usePagesAvailability = ({ translation, location }: Props) => {
  const { user } = useReactiveVar(authVar);

  const isAdmin = user?.isAdmin;
  const isEntraLess = user?.isEntraLess;
  const mobileDevices = user?.mobileDevices;
  const isRegistered = mobileDevices?.some((device) => device.role === 'ACCESS' && device.isRegistered);
  const is2FAEnabled = process.env.REACT_APP_IS_2FA_ENABLED === 'true';
  const isCardsEnabled = process.env.REACT_APP_IS_CARDS_ENABLED === 'true';

  const isRegistrationAvailable = () => !isRegistered;
  const isEventsAvailable = () => isAdmin;
  const isSettingsAvailable = () => Boolean(isAdmin);
  const isPermissionAvailable = () => Boolean(!isEntraLess);
  const isHardwareAvailable = () => Boolean(isAdmin);
  const isCardsAvailable = () => Boolean(isCardsEnabled);
  const isAccessAvailable = () => Boolean(isAdmin);
  const isUsersAvailable = () => Boolean(isAdmin);
  const isSecurityAvailable = () => Boolean(is2FAEnabled && isEntraLess);
  const isMyProfileAvailable = () => isRegistered || isAdmin;
  const isMobileRegistrationAvailable = () => Boolean(isMobile && !isRegistered);
  const isMobileMobileNotSupportedAvailable = () => Boolean(isMobile);

  const getSideNavItems = (): Array<MenuItem> => {
    const items: Array<Omit<MenuItem, 'isSelected'>> = [];

    if (isEventsAvailable())
      items.push({
        location: '/events',
        text: translation.events,
        icon: 'Booklet'
      });
    if (isHardwareAvailable())
      items.push({
        location: '/hardware',
        text: translation.hardware,
        icon: 'Hardware'
      });
    if (isAccessAvailable())
      items.push({
        location: '/access',
        text: translation.access,
        icon: 'Access'
      });
    if (isUsersAvailable())
      items.push({
        location: '/users',
        text: translation.users,
        icon: 'UserMenu'
      });
    if (isSettingsAvailable())
      items.push({
        location: '/settings',
        text: translation.settings,
        icon: 'Settings'
      });

    const itemsWithSelected = items.map((item) => ({
      ...item,
      isSelected: location.pathname.startsWith(item.location)
    }));

    return itemsWithSelected;
  };

  // TODO: move this to correct place
  const getNewSideNavItems = (): Array<MenuItem> => {
    const items: Array<Omit<MenuItem, 'isSelected'>> = [];
    if (isUsersAvailable())
      items.push({
        location: '/new/users',
        text: translation.users,
        icon: 'UserMenu'
      });
    const itemsWithSelected = items.map((item) => ({
      ...item,
      isSelected: location.pathname.startsWith(item.location)
    }));

    return itemsWithSelected;
  };

  return {
    isRegistrationAvailable,
    isEventsAvailable,
    isSettingsAvailable,
    isPermissionAvailable,
    isHardwareAvailable,
    isCardsAvailable,
    isAccessAvailable,
    isUsersAvailable,
    isMyProfileAvailable,
    isSecurityAvailable,
    getSideNavItems,
    getNewSideNavItems,
    isMobileRegistrationAvailable,
    isMobileMobileNotSupportedAvailable
  };
};

export default usePagesAvailability;
